// src/components/Lms/Utils/ProgressService.js
import axios from 'axios';
import { ENDPOINTS as LMS_ENDPOINTS } from './Endpoints';

/**
 * Obtiene el progreso de un estudiante en una lección
 */
export const getLeccionProgress = async (idLeccion, idEstudiante) => {
    try {
        const response = await axios.get(LMS_ENDPOINTS.leccionEstudiante);
        const leccionesEstudiante = response.data || [];

        const progreso = leccionesEstudiante.find(
            le => (le.idLeccion || le.IdLeccion) === parseInt(idLeccion, 10) &&
                (le.idEstudiante || le.IdEstudiante) === parseInt(idEstudiante, 10)
        );

        return progreso || null;
    } catch (error) {
        console.error('[ProgressService] Error obteniendo progreso:', error);
        return null;
    }
};

/**
 * Marca una lección como completada (Aprobado - IdEstado = 2)
 * @param {number} idLeccion - ID de la lección
 * @param {number} idEstudiante - ID del estudiante
 * @param {number} monedasGanadas - Monedas ganadas por completar la lección (default: 0)
 */
export const markLeccionAsCompleted = async (idLeccion, idEstudiante, monedasGanadas = 0) => {
    try {
        console.log(`[ProgressService] Marcando lección ${idLeccion} como completada para estudiante ${idEstudiante} con ${monedasGanadas} monedas`);

        // Verificar si ya existe un registro de lección
        const progresoExistente = await getLeccionProgress(idLeccion, idEstudiante);
        let debeAsignarMonedas = false;

        if (progresoExistente) {
            // Si existe pero NO estaba aprobado, asignar monedas
            const estadoActual = progresoExistente.idEstado || progresoExistente.IdEstado;
            if (estadoActual !== 2 && monedasGanadas > 0) {
                debeAsignarMonedas = true;
            }

            // Actualizar registro existente
            const idLeccionEstudiante = progresoExistente.idLeccionEstudiante || progresoExistente.IdLeccionEstudiante;

            const updated = {
                idLeccionEstudiante: idLeccionEstudiante,
                idLeccion: parseInt(idLeccion, 10),
                idEstudiante: parseInt(idEstudiante, 10),
                idEstado: 2,  // Aprobado
                monedasGanadas: parseInt(monedasGanadas, 10) || 0,
                activo: true
            };

            console.log('[ProgressService] Payload PUT LeccionEstudiante:', updated);
            await axios.put(LMS_ENDPOINTS.leccionEstudiante, updated);
            console.log('[ProgressService] Registro actualizado a Aprobado con monedas:', monedasGanadas);
        } else {
            // Nuevo registro - Asignar monedas si > 0
            if (monedasGanadas > 0) {
                debeAsignarMonedas = true;
            }

            // Crear nuevo registro
            const nuevo = {
                idLeccion: parseInt(idLeccion, 10),
                idEstudiante: parseInt(idEstudiante, 10),
                idEstado: 2,  // Aprobado
                monedasGanadas: parseInt(monedasGanadas, 10) || 0,
                activo: true
            };
            console.log('[ProgressService] Payload POST LeccionEstudiante:', nuevo);
            await axios.post(LMS_ENDPOINTS.leccionEstudiante, nuevo);
            console.log('[ProgressService] Nuevo registro creado como Aprobado con monedas:', monedasGanadas);
        }

        // --- ACTUALIZAR MONEDAS DEL ESTUDIANTE ---
        if (debeAsignarMonedas) {
            console.log(`[ProgressService] Asignando ${monedasGanadas} monedas al estudiante...`);
            // 1. Obtener estudiante actual
            // NOTA: Asumimos que LMS_ENDPOINTS.estudiante apunta al endpoint correcto
            // Necesitamos buscar el estudiante específico porque el GET suele devolver una lista o se necesita un ID.
            // Si LMS_ENDPOINTS.estudianteDevuelve lista, buscamos. Si acepta ID, mejor.
            // Para seguridad, obtendremos la lista y buscaremos por ID para asegurar tener el objeto completo.

            // Intentar GET por ID directo si es soportado, sino GET all
            // Probaremos GET all y buscar para evitar errores de endpoint no configurado para ID
            const resEstudiantes = await axios.get(LMS_ENDPOINTS.estudiante);
            const estudiantes = resEstudiantes.data || [];
            const estudiante = estudiantes.find(e => (e.idEstudiante || e.IdEstudiante) === parseInt(idEstudiante, 10));

            if (estudiante) {
                const saldoActual =
                    estudiante.monedas ?? estudiante.Monedas ?? estudiante.modenas ?? estudiante.Modenas ?? 0;
                const ganadasActual =
                    estudiante.monedasGanadas ?? estudiante.MonedasGanadas ?? estudiante.monedasAcumuladas ?? estudiante.MonedasAcumuladas ?? 0;

                const nuevoSaldo = (Number(saldoActual) || 0) + (Number(monedasGanadas) || 0);
                const nuevoGanadas = (Number(ganadasActual) || 0) + (Number(monedasGanadas) || 0);

                // Payload compatible: enviamos campos nuevos y legacy
                const estudiantePayload = {
                    idEstudiante: estudiante.idEstudiante ?? estudiante.IdEstudiante,
                    idEmpleado: estudiante.idEmpleado ?? estudiante.IdEmpleado,
                    avatarUrl: estudiante.avatarUrl ?? estudiante.AvatarUrl ?? '',
                    nickname: estudiante.nickname ?? estudiante.Nickname ?? '',
                    activo: estudiante.activo ?? estudiante.Activo ?? true,

                    // Saldo
                    Monedas: nuevoSaldo,
                    monedas: nuevoSaldo,
                    // Legacy saldo
                    modenas: nuevoSaldo,

                    // Acumuladas
                    MonedasGanadas: nuevoGanadas,
                    monedasGanadas: nuevoGanadas,
                    // Legacy acumuladas
                    MonedasAcumuladas: nuevoGanadas,
                    monedasAcumuladas: nuevoGanadas,

                    // Mantener si existe
                    MonedasGastadas: estudiante.monedasGastadas ?? estudiante.MonedasGastadas ?? 0,
                    monedasGastadas: estudiante.monedasGastadas ?? estudiante.MonedasGastadas ?? 0,
                };

                console.log('[ProgressService] Payload PUT Estudiante:', estudiantePayload);
                await axios.put(LMS_ENDPOINTS.estudiante, estudiantePayload);
                console.log(`[ProgressService] Estudiante actualizado. Nuevo saldo: ${nuevoSaldo}, MonedasGanadas: ${nuevoGanadas}`);
            } else {
                console.error('[ProgressService] No se encontró al estudiante para asignar monedas');
            }
        }

        return true;
    } catch (error) {
        console.error('[ProgressService] Error marcando lección como completada:', error);
        return false;
    }
};

/**
 * Marca un cuestionario como aprobado o reprobado basándose en la calificación
 * @param {number} idCuestionario - ID del cuestionario
 * @param {number} idEstudiante - ID del estudiante
 * @param {number} calificacion - Calificación obtenida (porcentaje 0-100)
 * @returns {Promise<boolean>}
 */
export const markQuizResult = async (idCuestionario, idEstudiante, calificacion) => {
    try {
        // Esta función ya no necesita interactuar con LeccionEstudiante.
        // El estado se deriva de los intentos guardados en CuestionarioIntentos.
        console.log(`[ProgressService] Cuestionario ${idCuestionario} registrado con calificación ${calificacion}%`);
        return true;
    } catch (error) {
        console.error('[ProgressService] Error en markQuizResult:', error);
        return false;
    }
};

/**
 * Marca un examen como aprobado o reprobado basándose en la calificación
 * @param {number} idExamen - ID del examen
 * @param {number} idEstudiante - ID del estudiante
 * @param {number} calificacion - Calificación obtenida (porcentaje 0-100)
 * @returns {Promise<boolean>}
 */
export const markExamResult = async (idExamen, idEstudiante, calificacion) => {
    try {
        // Esta función ya no necesita interactuar con LeccionEstudiante.
        // El estado se deriva de los intentos guardados en ExamenIntentos.
        console.log(`[ProgressService] Examen ${idExamen} registrado con calificación ${calificacion}%`);
        return true;
    } catch (error) {
        console.error('[ProgressService] Error en markExamResult:', error);
        return false;
    }
};

/**
 * Obtiene todas las lecciones completadas de un estudiante
 */
export const getCompletedLessons = async (idEstudiante) => {
    try {
        const response = await axios.get(LMS_ENDPOINTS.leccionEstudiante);
        const leccionesEstudiante = response.data || [];

        const completadas = leccionesEstudiante.filter(
            le => (le.idEstudiante || le.IdEstudiante) === parseInt(idEstudiante, 10) &&
                (le.idEstado || le.IdEstado) === 2  // Aprobado
        );

        return completadas;
    } catch (error) {
        console.error('[ProgressService] Error obteniendo lecciones completadas:', error);
        return [];
    }
};

/**
 * Obtiene todos los elementos reprobados de un estudiante
 */
export const getFailedLessons = async (idEstudiante) => {
    try {
        const response = await axios.get(LMS_ENDPOINTS.leccionEstudiante);
        const leccionesEstudiante = response.data || [];

        const reprobadas = leccionesEstudiante.filter(
            le => (le.idEstudiante || le.IdEstudiante) === parseInt(idEstudiante, 10) &&
                (le.idEstado || le.IdEstado) === 3  // Reprobado
        );

        return reprobadas;
    } catch (error) {
        console.error('[ProgressService] Error obteniendo elementos reprobados:', error);
        return [];
    }
};

/**
 * Obtiene los estados de cuestionarios basándose en el último intento
 * Retorna { aprobados: [ids], reprobados: [ids] }
 */
export const getQuizzesStatus = async (idEstudiante) => {
    try {
        const response = await axios.get(LMS_ENDPOINTS.cuestionariosIntentos);
        const intentos = response.data || [];

        // Filtrar intentos del estudiante
        const intentosEstudiante = intentos.filter(
            i => (i.idEstudiante || i.IdEstudiante) === parseInt(idEstudiante, 10)
        );

        // Agrupar por cuestionario y obtener el último intento
        const ultimosIntentos = {};
        intentosEstudiante.forEach(intento => {
            const idCuestionario = intento.idCuestionario || intento.IdCuestionario;
            const fecha = new Date(intento.fechaIntento || intento.FechaIntento);

            if (!ultimosIntentos[idCuestionario] || fecha > new Date(ultimosIntentos[idCuestionario].fechaIntento || ultimosIntentos[idCuestionario].FechaIntento)) {
                ultimosIntentos[idCuestionario] = intento;
            }
        });

        const aprobados = [];
        const reprobados = [];

        Object.values(ultimosIntentos).forEach(intento => {
            const calificacion = intento.calificacion || intento.Calificacion || 0;
            const idCuestionario = intento.idCuestionario || intento.IdCuestionario;

            if (calificacion >= 60) {
                aprobados.push(idCuestionario);
            } else {
                reprobados.push(idCuestionario);
            }
        });

        // console.log('[ProgressService] Cuestionarios aprobados:', aprobados);
        // console.log('[ProgressService] Cuestionarios reprobados:', reprobados);

        return { aprobados, reprobados };
    } catch (error) {
        console.error('[ProgressService] Error obteniendo estados de cuestionarios:', error);
        return { aprobados: [], reprobados: [] };
    }
};

/**
 * Obtiene los estados de exámenes basándose en el último intento
 * Retorna { aprobados: [ids], reprobados: [ids] }
 */
export const getExamsStatus = async (idEstudiante) => {
    try {
        const response = await axios.get(LMS_ENDPOINTS.examenesIntentos);
        const intentos = response.data || [];

        // Filtrar intentos del estudiante
        const intentosEstudiante = intentos.filter(
            i => (i.idEstudiante || i.IdEstudiante) === parseInt(idEstudiante, 10)
        );

        // Agrupar por examen y obtener el último intento
        const ultimosIntentos = {};
        intentosEstudiante.forEach(intento => {
            const idExamen = intento.idExamen || intento.IdExamen;
            const fecha = new Date(intento.fechaIntento || intento.FechaIntento);

            if (!ultimosIntentos[idExamen] || fecha > new Date(ultimosIntentos[idExamen].fechaIntento || ultimosIntentos[idExamen].FechaIntento)) {
                ultimosIntentos[idExamen] = intento;
            }
        });

        const aprobados = [];
        const reprobados = [];

        Object.values(ultimosIntentos).forEach(intento => {
            const calificacion = intento.calificacion || intento.Calificacion || 0;
            const idExamen = intento.idExamen || intento.IdExamen;

            if (calificacion >= 60) {
                aprobados.push(idExamen);
            } else {
                reprobados.push(idExamen);
            }
        });

        // console.log('[ProgressService] Exámenes aprobados:', aprobados);
        // console.log('[ProgressService] Exámenes reprobados:', reprobados);

        return { aprobados, reprobados };
    } catch (error) {
        console.error('[ProgressService] Error obteniendo estados de exámenes:', error);
        return { aprobados: [], reprobados: [] };
    }
};

/**
 * Calcula el progreso del estudiante en un curso
 */
export const calculateCourseProgress = async (idEstudiante, curso) => {
    try {
        if (!curso || !curso.modulos) {
            return { completadas: 0, total: 0, porcentaje: 0 };
        }

        // Obtener todos los elementos del curso por tipo
        const items = curso.modulos.flatMap(m => (m.contenido || []));

        const leccionesDelCurso = items
            .filter(c => c.tipo === 'LECCION')
            .map(c => c.idLeccion || c.id);

        const quizzesDelCurso = items
            .filter(c => c.tipo === 'CUESTIONARIO')
            .map(c => c.idCuestionario || c.id);

        const examenesDelCurso = items
            .filter(c => c.tipo === 'EXAMEN')
            .map(c => c.idExamen || c.id);

        const total = leccionesDelCurso.length + quizzesDelCurso.length + examenesDelCurso.length;
        if (total === 0) {
            return { completadas: 0, total: 0, porcentaje: 0 };
        }

        // Calcular completados (aprobados) por tipo
        const [todasCompletadas, quizzesStatus, examsStatus] = await Promise.all([
            getCompletedLessons(idEstudiante),
            getQuizzesStatus(idEstudiante),
            getExamsStatus(idEstudiante)
        ]);

        const leccionesAprobadas = todasCompletadas
            .map(le => le.idLeccion || le.IdLeccion)
            .filter(id => leccionesDelCurso.some(lcId => String(lcId) === String(id)));

        const quizzesAprobados = (quizzesStatus.aprobados || [])
            .filter(id => quizzesDelCurso.some(qId => String(qId) === String(id)));

        const examenesAprobados = (examsStatus.aprobados || [])
            .filter(id => examenesDelCurso.some(eId => String(eId) === String(id)));

        // Usar keys compuestas para evitar colisiones de IDs
        const completadasKeys = new Set([
            ...leccionesAprobadas.map((id) => `LECCION-${id}`),
            ...quizzesAprobados.map((id) => `CUESTIONARIO-${id}`),
            ...examenesAprobados.map((id) => `EXAMEN-${id}`)
        ]);

        const completadas = completadasKeys.size;
        const porcentaje = (completadas / total) * 100;

        // console.log(`[ProgressService] Progreso calculado: ${completadas}/${total} = ${Math.round(porcentaje)}%`);

        return {
            completadas,
            total,
            porcentaje: Math.round(porcentaje)
        };
    } catch (error) {
        console.error('[ProgressService] Error calculando progreso:', error);
        return { completadas: 0, total: 0, porcentaje: 0 };
    }
};

export const calculateAverageExamGrade = async (idEstudiante, curso) => {
    try {
        if (!curso || !curso.modulos) {
            return { promedio: 0, totalExamenes: 0 };
        }

        const items = curso.modulos.flatMap(m => (m.contenido || []));
        const examenesDelCurso = items
            .filter(c => c.tipo === 'EXAMEN')
            .map(c => c.idExamen || c.id);

        const totalExamenes = examenesDelCurso.length;
        if (totalExamenes === 0) {
            return { promedio: 0, totalExamenes: 0 };
        }

        const response = await axios.get(LMS_ENDPOINTS.examenesIntentos);
        const intentos = response.data || [];

        const intentosEstudiante = intentos.filter(
            i => (i.idEstudiante || i.IdEstudiante) === parseInt(idEstudiante, 10)
        );

        const ultimosIntentos = {};
        intentosEstudiante.forEach(intento => {
            const idExamen = intento.idExamen || intento.IdExamen;
            if (!examenesDelCurso.includes(idExamen)) return;

            const fecha = new Date(intento.fechaIntento || intento.FechaIntento);
            const current = ultimosIntentos[idExamen];
            if (!current) {
                ultimosIntentos[idExamen] = intento;
                return;
            }
            const fechaCurrent = new Date(current.fechaIntento || current.FechaIntento);
            if (fecha > fechaCurrent) {
                ultimosIntentos[idExamen] = intento;
            }
        });

        let sum = 0;
        examenesDelCurso.forEach((idExamen) => {
            const intento = ultimosIntentos[idExamen];
            const calificacion = intento ? (intento.calificacion || intento.Calificacion || 0) : 0;
            sum += Number(calificacion) || 0;
        });

        const promedio = Math.round(sum / totalExamenes);
        return { promedio, totalExamenes };
    } catch (error) {
        console.error('[ProgressService] Error calculando promedio de exámenes:', error);
        return { promedio: 0, totalExamenes: 0 };
    }
};

/**
 * Verifica si una lección específica está completada
 */
export const isLeccionCompleted = async (idLeccion, idEstudiante) => {
    try {
        const progreso = await getLeccionProgress(idLeccion, idEstudiante);
        return progreso && (progreso.idEstado || progreso.IdEstado) === 2;
    } catch (error) {
        console.error('[ProgressService] Error verificando si lección está completada:', error);
        return false;
    }
};
